import moment from "moment";
import Bowser from "bowser";
import {
  RESULT_DIRECT_SUCCESS,
  RESULT_INDIRECT_SUCCESS,
  RESULT_DIRECT_FAILURE,
  RESULT_INDIRECT_FAILURE,
  RESULT_SUCCESS,
  TESTER_CATEGORY,
  CARD_TYPE_OPEN,
  QUEST_TYPE_TEXT,
  OPTION_TYPE_OTHERS,
  CARD_TYPE_HYBRID,
  RESULT_DIRECT_SKIP,
  RESULT_INDIRECT_SKIP,
  MERGED_CATEGORY,
  INCLUDE_PARTICIPANT,
  RESULT_FAILED,
  DIRECTION_FORWARD,
  DIRECTION_BACKWARD,
} from "../actions/constants";
import { FORWARD, BACKWARD, SIBLING } from "./icons";
import { mean, mode } from "../utills/common";
import countries from "./countries";

export const getParticipantEmail = (participant) => (participant.firstName ? participant.firstName : "-");

// Returns duration in Seconds
export const calculateTimeTaken = (start, end, durationOnly = false) => {
  let duration = moment.duration(moment(end).diff(moment(start)));
  return durationOnly ? duration.asSeconds() : getDurationInMMSS(duration);
};

export const getDurationInMMSS = (duration) => {
  if (!isFinite(duration)) return "0 Sec";

  if (!isNaN(parseInt(duration))) duration = moment.duration(duration * 1000);

  let hhmmss = "";
  if (duration.get("hours") > 0) hhmmss += duration.get("hours") + " Hour ";
  if (duration.get("minutes") > 0) hhmmss += duration.get("minutes") + " Min ";
  if (duration.get("seconds") >= 0) hhmmss += duration.get("seconds") + " Sec";

  return hhmmss.trim();
};

export const dirctnessLabel = (type) => {
  const labels = {
    directSuccess: "Direct Success",
    indirectSuccess: "Indirect Success",
    directFail: "Direct Fail",
    indirectFail: "Indirect Fail",
    directSkip: "Direct Skip",
    indirectSkip: "Indirect Skip",
  };

  return labels[type] ? labels[type] : "";
};

export const getResultType = (result) => {
  const types = { 1: "directSuccess", 2: "indirectSuccess", 3: "directFail", 4: "indirectFail", 5: "directSkip", 6: "indirectSkip" };

  return types[result] ? types[result] : "directFail";
};

export const getLabelOptions = () => [
  {
    color: "#2CBFCA",
    key: "directSuccess",
    text: "Direct success",
    className: "color-2CBFCA",
    value: RESULT_DIRECT_SUCCESS,
  },
  {
    color: "#93CDD1",
    key: "indirectSuccess",
    text: "Indirect success",
    className: "color-93CDD1",
    value: RESULT_INDIRECT_SUCCESS,
  },
  {
    color: "#F53D6B",
    key: "directFail",
    text: "Direct failure",
    className: "color-F53D6B",
    value: RESULT_DIRECT_FAILURE,
  },
  {
    isLast: true,
    color: "#FBB1C4",
    key: "indirectFail",
    text: "Indirect failure",
    className: "color-FBB1C4",
    value: RESULT_INDIRECT_FAILURE,
  },
  { key: "directSkip", text: "Direct skip", value: RESULT_DIRECT_SKIP, className: "color-FFC233", color: "#FFC233" },
  {
    isLast: true,
    color: "#FFE7AD",
    key: "indirectSkip",
    text: "Indirect skip",
    className: "color-FFE7AD",
    value: RESULT_INDIRECT_SKIP,
  },
];

export const getResultObj = (result) => getLabelOptions().find((l) => l?.value === result);

export const getResultText = (result) => {
  const item = getLabelOptions().find((label) => label.value === result);
  return item ? item.text : "NA";
};

export const getPathLegends = () => [
  { key: "forward", text: "Forward", icon: FORWARD },
  { key: "backward", text: "Backward", icon: BACKWARD },
  { key: "sibling", text: "Sibling", icon: SIBLING, isLast: true },
];

// Get Result ClassName
export const taskResultClassName = (status) => {
  switch (status) {
    case RESULT_DIRECT_SUCCESS:
      return "text-success-3";
    case RESULT_INDIRECT_SUCCESS:
      return "text-success-4";
    case RESULT_DIRECT_FAILURE:
      return "text-red-6";
    case RESULT_INDIRECT_FAILURE:
      return "text-red-7";
    case 5:
      return "text-blue-16";
    case 6:
      return "text-blue-6";
    default:
      return "text-red-6";
  }
};

// Get Country
export const getCountryName = (countryCode) => {
  const country = countries.find((item) => item.value === countryCode);
  return country ? country.text : "";
};

// Get Avg From Array
export const getAvgFromArray = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;

// Tasks each participant is shown, honouring the tasks per participant setting
export const getTasksPerParticipant = (test) => {
  const totalTasks = test?.tasks?.length || 0;
  const tasksToShow = Number(test?.settings?.tasks_to_show) || 0;
  return tasksToShow > 0 && tasksToShow < totalTasks ? tasksToShow : totalTasks;
};

// A completed response missing the task never saw it (tasks per participant, possibly changed after launch)
export const isTaskShown = (res, taskId) => res?.status !== RESULT_SUCCESS || !!res?.taskAnswers?.some((answer) => answer.id === taskId);

const PATH_DIRECTIONS = {
  [DIRECTION_FORWARD]: { icon: "forward", text: ">>" },
  [DIRECTION_BACKWARD]: { icon: "backward", text: "<<<<" },
};
const SIBLING_DIRECTION = { icon: "sibling", text: "<<-->>" };

// One participant's path and timing for a tree test task; steps after the first carry their direction icon/text
export const getTaskPathDetails = (res, taskId) => {
  const details = { result: RESULT_FAILED, answer: "-", answerObj: [], answerArr: [], answerText: "", timeTaken: 0, timeTakenText: "-" };
  const userResult = res?.taskAnswers?.find((item) => item.id === taskId);
  if (!userResult) return details;

  const { answer = [], skipped, startTime, endTime, result } = userResult;
  details.result = result ?? RESULT_FAILED;

  if (skipped && answer.length <= 1) details.answer = "Skipped";
  else {
    details.answer = "";
    details.answerObj = answer;
    details.answerArr = answer.map(({ label, direction }, index) => ({
      label,
      direction: index > 0 ? PATH_DIRECTIONS[direction || DIRECTION_FORWARD] || SIBLING_DIRECTION : null,
    }));
    details.answerText = details.answerArr.map(({ label, direction }) => (direction ? `${direction.text} ${label}` : label)).join(" ");
  }

  if (startTime && endTime) {
    details.timeTaken = endTime - startTime;
    details.timeTakenText = calculateTimeTaken(new Date(startTime).toISOString(), new Date(endTime).toISOString());
  }

  return details;
};

// Responses in which the given task was shown
export const getTaskResponses = (responses = [], taskId) => responses.filter((res) => isTaskShown(res, taskId));

// True when the setting limits tasks now or any response missed a task
export const hasTaskSubset = (test, responses = []) =>
  getTasksPerParticipant(test) < (test?.tasks?.length || 0) ||
  responses.some((res) => test?.tasks?.some((task) => !isTaskShown(res, task._id)));

export const getTaskSuccessfullRate = (item, totalTasks) => {
  const successTasks = item.taskAnswers.filter(
    (taskAns) => taskAns.result === RESULT_DIRECT_SUCCESS || taskAns.result === RESULT_INDIRECT_SUCCESS
  );

  // Completed responses keep the task count they were shown, even if the setting changed later
  const shownTasks = item.status === RESULT_SUCCESS && item.taskAnswers.length ? item.taskAnswers.length : totalTasks;
  return `${successTasks.length} of ${shownTasks} Tasks`;
};

export const testCompeletion = (item, totalTasks) => parseInt((item.taskAnswers.length / totalTasks) * 100) + "%";

export const getDeviceDetails = (os, browser, onlyBrowser = false) => {
  const browserName = browser.name + " " + (browser.version ? parseInt(browser.version) : "");
  if (onlyBrowser) return browserName;

  let device = os.name;
  if (device.toLowerCase() === "windows" && os.versionName) device += " " + os.versionName;
  else if (os.version) device += " " + os.version;
  return device + ", " + browserName;
};

// "OS version, Browser version" from a user agent, optionally prefixed with the platform (desktop/mobile)
export const getDeviceLabel = (userAgent, withPlatform = false) => {
  if (!userAgent) return "NA";
  const { browser, os, platform } = Bowser.parse(userAgent);
  return (withPlatform && platform.type ? platform.type + ", " : "") + getDeviceDetails(os, browser);
};

export const getPlatformType = (type) => {
  if (!type) return "";
  type = type.toLowerCase();
  if (type !== "desktop") return type + " alternate";
  return type;
};

export const calculateResultMatrix = (responses = [], card_sort = {}, settings = {}, considerUnsortedCards = false) => {
  let { cards = [], categories = [] } = card_sort || {};

  // In case of open card sort, only consider tester categories
  if (settings?.type === CARD_TYPE_OPEN) {
    categories = categories?.filter((cat) => Number(cat?.type) === TESTER_CATEGORY || Number(cat?.type) === MERGED_CATEGORY);
  }

  // To remove category without names(mainly abandoned response)
  if (settings?.type === CARD_TYPE_OPEN || settings?.type === CARD_TYPE_HYBRID) {
    categories = categories?.filter((cat) => cat?.hasOwnProperty("name"));
  }

  const result = cards.map((card) => {
    const res = responses.reduce((res, resp) => {
      const { categories } = resp?.cardSort || resp?.answer || {};
      for (let i in categories) {
        const isExists = categories[i].reduce((exists, item) => {
          if (exists) return exists;
          return item.id === card?._id || item?.id === card?.id;
        }, false);

        if (isExists) {
          if (!res[i]) res[i] = 0;
          res[i] += 1;
        }
      }
      return res;
    }, {});

    const catData = [];
    for (let i in res) {
      const category = categories.find((cat) => cat?._id === i || cat?.id === i);
      catData.push({ ...category, frequency: res[i] });
    }

    return { ...card, categories: catData };
  });

  const minFrequencyRequiredToColor = 2; // no need to make frequency less than this constant to highest, and no need to color it
  result.forEach((res) => {
    let first = 0,
      second = 0,
      third = 0;
    res.categories.forEach((cat) => {
      if (cat.frequency > first) {
        third = second;
        second = first;
        first = cat.frequency;
      } else if (cat.frequency > second) {
        third = second;
        second = cat.frequency;
      } else if (cat.frequency > third) {
        third = cat.frequency;
      }

      // calculate popular placement matrix
      cat.popularPlacementMatrix = responses.length ? ((cat.frequency * 100) / responses.length).toFixed(1) : 0;
    });

    // assign highest value 1,2,3 such that for each similar frequencies the highest should remain same always
    let ishighest1Set = false,
      ishighest2Set = false,
      isHeightest3Set = false;
    res.categories.map((cat) => {
      if (cat.frequency > minFrequencyRequiredToColor) {
        if (!ishighest1Set && cat.frequency === first) {
          cat.highest = 1;
          ishighest1Set = true;
        } else if (!ishighest2Set && cat.frequency === second) {
          if (cat.frequency === first) {
            cat.highest = 1;
          } else {
            cat.highest = 2;
          }
          ishighest2Set = true;
        } else if (!isHeightest3Set && cat.frequency === third) {
          if (cat.frequency === first) {
            cat.highest = 1;
          } else if (cat.frequency === second) {
            cat.highest = 2;
          } else {
            cat.highest = 3;
          }
          isHeightest3Set = true;
        }
      }
      return cat;
    });

    if (considerUnsortedCards) {
      // how many times the card is left unsorted
      let sortedCount = 0;
      res.categories.forEach((cat) => (sortedCount += cat.frequency));
      const unsortedCount = responses.length - sortedCount;

      if (unsortedCount) {
        res.categories.push({
          id: "unsorted",
          _id: "unsorted",
          name: "Unsorted",
          frequency: unsortedCount,
          popularPlacementMatrix: responses.length ? ((unsortedCount * 100) / responses.length).toFixed(1) : 0,
        });
      }
    }
  });

  if (considerUnsortedCards) {
    // unsorted category
    if (!categories.find((cat) => cat?._id === "unsorted" || cat?.id === "unsorted")) {
      categories.push({ id: "unsorted", _id: "unsorted", name: "Unsorted" });
    }
  }

  return { cards, categories, result };
};

export const calculateConfidenceInSorting = (response) => {
  const CONFIDENCE_LOW = 1;
  const CONFIDENCE_MEDIUM = 2;
  const CONFIDENCE_HIGH = 3;

  const cardsTravelHistory = {};
  response.forEach((res) => {
    const resId = res?._id;
    if (cardsTravelHistory[resId] === undefined || !cardsTravelHistory[resId]) cardsTravelHistory[resId] = { mean: 0, mode: [] };

    if (res?.cardSort && res?.cardSort?.cards) {
      let pathLengthArray = [];
      Object.keys(res.cardSort.cards).forEach((cardId) => {
        pathLengthArray.push(res.cardSort.cards[cardId].length);
        if (cardsTravelHistory[resId][cardId] === undefined || !cardsTravelHistory[resId][cardId])
          cardsTravelHistory[resId][cardId] = { pathLength: 0, confidence: "" };
        cardsTravelHistory[resId][cardId].pathLength = res.cardSort.cards[cardId].length;
      });

      const MEAN = mean(pathLengthArray);
      const MODE = mode(pathLengthArray);

      cardsTravelHistory[resId].mean = MEAN;
      cardsTravelHistory[resId].mode = MODE;

      Object.keys(res.cardSort.cards).forEach((cardId) => {
        const pathLength = cardsTravelHistory[resId][cardId].pathLength;
        let confidence = "";

        if (MODE.length === 1) {
          // Only one mode is available for the series
          if (pathLength === MODE[0]) {
            if (pathLength >= MEAN) confidence = CONFIDENCE_MEDIUM;
            else confidence = CONFIDENCE_HIGH;
          } else if (pathLength < MODE[0]) {
            confidence = CONFIDENCE_HIGH;
          } else {
            confidence = CONFIDENCE_LOW;
          }
        } else {
          // Mode is ambiguous
          if (pathLength === MEAN) {
            confidence = CONFIDENCE_MEDIUM;
          } else if (pathLength < MEAN) {
            confidence = CONFIDENCE_HIGH;
          } else {
            confidence = CONFIDENCE_LOW;
          }
        }

        cardsTravelHistory[resId][cardId].confidence = confidence;
      });
    }
  });

  const cardConfidenceMatrix = {};
  Object.keys(cardsTravelHistory).forEach((userId) => {
    Object.keys(cardsTravelHistory[userId]).forEach((cardId) => {
      if (cardId !== "mean" && cardId !== "mode") {
        if (cardConfidenceMatrix[cardId] === undefined || !cardConfidenceMatrix[cardId])
          cardConfidenceMatrix[cardId] = { individualUsersConfidence: [], confidence: "" };
        cardConfidenceMatrix[cardId].individualUsersConfidence.push(cardsTravelHistory[userId][cardId].confidence);
      }
    });
  });

  Object.keys(cardConfidenceMatrix).forEach((cardId) => {
    const overallConfidence = mode(cardConfidenceMatrix[cardId].individualUsersConfidence)[0];
    let conf = "";
    switch (overallConfidence) {
      case CONFIDENCE_LOW:
        conf = "Low";
        break;
      case CONFIDENCE_MEDIUM:
        conf = "Medium";
        break;
      case CONFIDENCE_HIGH:
        conf = "High";
        break;
      default:
        conf = "";
        break;
    }
    cardConfidenceMatrix[cardId].confidence = conf;
  });

  return cardConfidenceMatrix;
};

export const getSurveyResultData = (response, test, type) => {
  const surveyQuestions = test && test.survey ? test.survey[type] : [];
  return surveyQuestions.map((question) => {
    const { question_type } = question;
    const questionResult = response.reduce((resp, { _id: responseId, testSurveyAnswers, participant, endTime, included }) => {
      const answer = testSurveyAnswers?.[type]?.find((res) => res.id === question._id);
      if (!answer) return resp;

      // Radio/Checkbox answers hold option ids; build the text on a copy so the stored response isn't mutated
      const answerText = Array.isArray(answer.answer) && answer.answer.length > 0
        ? question.option.filter((opt) => answer.answer.includes(opt._id)).map((opt) => opt.label).join(", ")
        : answer.answerText;

      return [...resp, { ...answer, answerText, responseId, participant, endTime, included }];
    }, []);
    const option =
      question_type === QUEST_TYPE_TEXT
        ? question.option
        : question.option.map((opt) => ({
            ...opt,
            result: questionResult.reduce(
              (response, resp) => {
                const { answer, text } = resp;
                let { count, others } = response;
                if (answer.includes(opt._id)) count++;
                if (opt.type === OPTION_TYPE_OTHERS && text.trim()) others.push(text);
                return { ...response, count, others };
              },
              { count: 0, others: [] }
            ),
          }));

    const barData = question_type === QUEST_TYPE_TEXT ? [] : option.map((opt) => ({ name: opt.label, Total: opt.result.count }));
    return { ...question, option, questionResult, barData };
  });
};

// Confirmed with the original toolkit dev: a real name (fullName), when known, has always taken
// priority over the anonymous P1/P2 label - that's existing, intended app behavior, not something
// the CLP-id feature below should override. The CLP id is an ADDITIONAL annotation on top of
// whichever label wins, not a replacement for it - so it's appended here rather than baked into the
// plain `name` field, which every consumer still needs in its bare P1/P2 form (e.g. for sorting).
export const getParticipantLabel = (participant = {}) => {
  const base = participant?.fullName || participant?.name || "";
  return participant?.clientContactDisplayId ? `${base} (${participant.clientContactDisplayId})` : base;
};

export const createDesiredResponse = (response) => {
  response = response.map((res, index) => {
    const label = `P${res.participant.respIndex ? res.participant.respIndex : index + 1}`;
    // A PHUB invitee skips the demographics form entirely (see Welcome.jsx's isPhubTester check),
    // so firstName/lastName are never set for them - unconditionally rebuilding fullName from those
    // two fields wiped out the real contact name enrichToolkitResponsesWithContacts had already put
    // here (client-contacts.service.ts), leaving every PHUB response showing its anonymous P# label
    // instead of the contact's actual name. clientContactDisplayId (CLP-xxxxxx) is only ever present
    // on enriched PHUB data, so it's what tells apart "no name typed yet" from "this participant's
    // real name lives in fullName already, leave it alone".
    const participant = {
      ...res.participant,
      fullName: res.participant?.firstName
        ? `${res.participant?.firstName} ${res.participant?.lastName}`
        : res.participant?.clientContactDisplayId
        ? res.participant?.fullName || ""
        : "",
      name: label,
    };

    const { others } = participant;

    if (others && others.age) {
      let ageGroup = "";
      const { age } = others;
      if (age < 21) ageGroup = "Below 21 years";
      else if (age >= 21 && age <= 30) ageGroup = "21-30 years";
      else if (age >= 31 && age <= 40) ageGroup = "31-40 years";
      else if (age >= 41 && age <= 54) ageGroup = "41-54 years";
      else if (age > 54) ageGroup = "Above 54 years";

      if (ageGroup) participant.others = { ...others, ageGroup };
    }
    return { ...res, participant };
  });

  const allResponses = response?.toReversed();
  const includedResponses = allResponses.filter((resp) => resp.included === INCLUDE_PARTICIPANT.YES);
  return {
    includedResponses,
    response: allResponses,
    reportResponses: includedResponses.filter((resp) => resp.status === RESULT_SUCCESS),
  };
};

// Full name when demographics were collected, otherwise the P-number
export const getParticipantName = (participant) => participant?.fullName || participant?.name || "";

export const getMemberId = (queryParams) =>
  queryParams && (queryParams.memberId || queryParams.memberID || queryParams.memberid || queryParams.member_id);

export const getEndTimeText = (endTime, returnTimestamp = false) => {
  if (endTime) return returnTimestamp ? moment(endTime).unix() : moment(endTime).format("Do MMM, YYYY hh:mm A");
  return returnTimestamp ? 0 : "--";
};

export const checkParticipantUpdateData = (resp) => {
  const initial = {
    isAge: false,
    isGender: false,
    isEthnicity: false,
    isMemberID: false,
    isDataUpdated: false,
    isNameUpdated: false,
    isNationality: false,
  };

  return resp.reduce((result, item) => {
    const { queryParams, isDataUpdated, dataUpdated, gender, nationality, others } = item.participant;

    // Is Member ID Exists
    if (!result.isMemberID) {
      const memberID = getMemberId(queryParams);
      result.isMemberID = (queryParams && Object.keys(queryParams).length !== 0) || memberID ? true : false;
    }

    if (!result.isGender) result.isGender = gender !== undefined;
    if (!result.isAge && others) result.isAge = others.age !== undefined;
    if (!result.isDataUpdated && isDataUpdated) result.isDataUpdated = true;
    if (!result.isNationality) result.isNationality = nationality !== undefined;
    if (!result.isEthnicity && others) result.isEthnicity = others.ethnicity !== undefined;
    if (!result.isNameUpdated) result.isNameUpdated = isDataUpdated !== undefined && dataUpdated.includes("firstName");

    return result;
  }, initial);
};

// Get Most Wrong/First Click Node
export const getMostFCorWGNode = (treeNodeData, type, taskId) => {
  return treeNodeData.reduce((findNode, item) => {
    if (item.tasks) {
      const node = item.tasks.find((task) => task.id === taskId);

      // Add Node if not exists
      if (node?.[type] && !findNode) return item;

      // Get worngCount
      if (findNode && findNode.tasks) {
        const previousNode = findNode.tasks.find((task) => task.id === taskId);

        // Compare node with previous added node
        return node?.[type] && node[type] > (previousNode?.[type] || 0) ? item : findNode;
      }
    }

    return findNode;
  }, null);
};

export const getPercentValue = (value, total, valueOnly = false) => {
  const division = total > 0 ? value / total : 0;
  // Guard against NaN / Infinity (e.g. total = 0 or non-numeric input)
  const percentValue = Number.isFinite(division) ? Number((division * 100).toFixed(2)) : 0;

  return valueOnly ? percentValue : `${percentValue}%`;
};

// Get Device Details
export const getAllDevices = (responses = []) => {
  const dataWithPercent = (d) =>
    d?.map((item) => ({ ...item, percent: Number(getPercentValue(item?.count, responses?.length, true)) }));

  return responses?.reduce(
    (obj, { participant }) => {
      if (participant && participant?.userAgent) {
        const { os, browser, platform } = Bowser.parse(participant?.userAgent);

        const osn = os?.name ? os?.name?.toLowerCase()?.trim() : "";
        const osname = osn === "macos" || osn === "ios" ? "ios" : osn;
        const browserName = browser?.name ? browser?.name?.toLowerCase()?.trim() : "";
        const device = platform?.type ? platform?.type?.toLowerCase()?.trim() : "desktop";

        // Os
        const osKey = osname === "chrome os" ? "chrome" : osname;
        const prevOsObj = obj?.oss?.data?.find((d) => d?.key === osKey && d?.type === "os");
        if (prevOsObj) prevOsObj.count = (prevOsObj?.count || 0) + 1;
        else obj.oss.data.push({ key: osKey, name: osname === "ios" ? "Apple" : os?.name, count: 1, type: "os" });

        // Device
        const deviceKey = `${device}1`;
        const prevDeviceObj = obj?.devices?.data?.find((d) => d?.key === deviceKey && d?.type === "device");
        if (prevDeviceObj) prevDeviceObj.count = (prevDeviceObj?.count || 0) + 1;
        else
          obj.devices.data.push({ count: 1, type: "device", key: deviceKey, name: device.charAt(0).toUpperCase() + device.slice(1) });

        // Browser
        const validBrowserNames = ["chrome", "firefox", "safari", "microsoft edge", "edge"];
        const browserNameKey = `${validBrowserNames.includes(browserName) ? browserName : "chrome"}`?.replaceAll(" ", "_");
        const prevBrowserObj = obj?.browsers?.data?.find((d) => d?.key === browserNameKey && d?.type === "browser");
        if (prevBrowserObj) prevBrowserObj.count = (prevBrowserObj?.count || 0) + 1;
        else obj.browsers.data.push({ key: browserNameKey, name: browser?.name, count: 1, type: "browser" });
      }

      return {
        oss: { ...obj?.oss, data: dataWithPercent(obj?.oss?.data) },
        devices: { ...obj?.devices, data: dataWithPercent(obj?.devices?.data) },
        browsers: { ...obj?.browsers, data: dataWithPercent(obj?.browsers?.data) },
      };
    },
    { devices: { name: "Devices", data: [] }, browsers: { name: "Browsers", data: [] }, oss: { name: "Operating System", data: [] } }
  );
};

// How often each card was sorted into each category, keyed `${categoryId}|${cardId}`
export const getCardCategoryCounts = (responses = []) => {
  const counts = new Map();
  responses.forEach((resp) => {
    const { categories = {} } = resp?.cardSort || resp?.answer || {};
    Object.entries(categories).forEach(([catId, cards = []]) =>
      cards.forEach(({ id }) => counts.set(`${catId}|${id}`, (counts.get(`${catId}|${id}`) || 0) + 1))
    );
  });
  return counts;
};

// Card/category images are a plain path on TBT and an uploaded image list elsewhere
export const getImagePath = (image, isTBT = false) => (isTBT ? image : image?.[0]?.path);

// Number of participants who sorted each pair of cards into the same category, keyed by both id orders
export const getCardPairCounts = (responses = []) => {
  const counts = new Map();
  responses.forEach((resp) => {
    const pairs = new Set();
    Object.values(resp?.cardSort?.categories || {}).forEach((group = []) => {
      const ids = group.map(({ id }) => id);
      ids.forEach((a, i) => ids.slice(i + 1).forEach((b) => pairs.add(`${a}|${b}`).add(`${b}|${a}`)));
    });
    pairs.forEach((key) => counts.set(key, (counts.get(key) || 0) + 1));
  });
  return counts;
};

// First clicks and destinations per tree node and task, from completed included responses.
// Single home node: path[0] is pre-seeded (not clicked), so the first click is path[1]; with several, it's path[0].
export const getTreeClickCounts = (treeData = [], tasks = [], responses = []) => {
  const fclicks = new Map();
  const success = new Map();
  const wrong = new Map();
  const bump = (map, key) => map.set(key, (map.get(key) || 0) + 1);
  const firstClickIndex = treeData.length === 1 ? 1 : 0;
  const answersByTask = new Map(tasks.map((task) => [String(task._id), task.answers || []]));

  responses.forEach((res) =>
    (res.taskAnswers || []).forEach(({ id, answer: path = [] }) => {
      const taskId = String(id);
      if (!answersByTask.has(taskId) || !path.length) return;
      if (path.length > firstClickIndex) bump(fclicks, `${path[firstClickIndex].id}|${taskId}`);
      const last = path[path.length - 1];
      bump(answersByTask.get(taskId).includes(last.id) ? success : wrong, `${last.id}|${taskId}`);
    })
  );

  // Same node.tasks shape the report used to receive from the API
  const loop = (nodes = []) =>
    nodes.map((node) => {
      const isLeaf = !node.children?.length;
      return {
        ...node,
        children: isLeaf ? node.children : loop(node.children),
        tasks: tasks.map(({ _id, answers = [] }) => {
          const key = `${node.id}|${String(_id)}`;
          const counts = { id: _id, fcount: fclicks.get(key) || 0 };
          return isLeaf ? { ...counts, scount: success.get(key) || 0, wcount: wrong.get(key) || 0, answers } : counts;
        }),
      };
    });

  return loop(treeData);
};
